import React, { useContext, useState } from 'react';
import { LanguageContext } from '../components/LanguageContext';
import awardWinners from '../data/awardWinners.json';

const Schemes = () => {
  const { language } = useContext(LanguageContext);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('awards');

  const getWinnersForAward = (awardName) => {
    const winners = {};
    Object.entries(awardWinners).forEach(([year, yearWinners]) => {
      const matchingWinners = yearWinners.filter(w => w.award === awardName);
      if (matchingWinners.length > 0) {
        winners[year] = matchingWinners;
      }
    });
    return winners;
  };

  const WinnersModal = ({ scheme, onClose }) => {
    if (!scheme) return null;
    
    // For Lifetime Achievement Award, handle sub-awards
    if (scheme.awardName === "LIFETIME ACHIEVEMENT AWARD" && scheme.subAwards) {
      const allWinners = {};
      
      // Collect winners for all sub-awards
      scheme.subAwards.forEach(subAward => {
        const subAwardWinners = getWinnersForAward(subAward.awardName);
        
        // Merge with main winners object, adding award name to each winner
        Object.entries(subAwardWinners).forEach(([year, yearWinners]) => {
          if (!allWinners[year]) allWinners[year] = [];
          
          // Add award name to each winner
          const winnersWithAwardName = yearWinners.map(winner => ({
            ...winner,
            subAwardName: subAward.name
          }));
          
          allWinners[year] = [...allWinners[year], ...winnersWithAwardName];
        });
      });
      
      const hasWinners = Object.keys(allWinners).length > 0;
      
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex justify-between items-start">
                <h3 className="text-xl font-semibold text-gray-900">{scheme.title}</h3>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {hasWinners ? (
                <div className="space-y-6">
                  {Object.entries(allWinners).reverse().map(([year, yearWinners]) => (
                    <div key={year} className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-lg font-medium text-blue-700 mb-3">{year}</h4>
                      <div className="space-y-3">
                        {yearWinners.map((winner, idx) => (
                          <div key={idx} className="flex flex-col bg-white p-3 rounded-md shadow-sm">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-gray-900 font-medium">{winner.name}</span>
                              <span className="text-gray-500 text-sm">{winner.district}</span>
                            </div>
                            <span className="text-blue-600 text-sm">{winner.subAwardName}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">
                  {language === 'en' 
                    ? 'No winners recorded yet' 
                    : 'ابھی تک کوئی فاتح درج نہیں ہوا'}
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    // Regular awards (non-lifetime achievement)
    const winners = scheme.awardName ? getWinnersForAward(scheme.awardName) : {};
    const hasWinners = Object.keys(winners).length > 0;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
          <div className="p-6 border-b">
            <div className="flex justify-between items-start">
              <h3 className="text-xl font-semibold text-gray-900">{scheme.title}</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {hasWinners ? (
              <div className="space-y-6">
                {Object.entries(winners).reverse().map(([year, yearWinners]) => (
                  <div key={year} className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-lg font-medium text-blue-700 mb-3">{year}</h4>
                    <div className="space-y-3">
                      {yearWinners.map((winner, idx) => (
                        <div key={idx} className="flex items-start justify-between bg-white p-3 rounded-md shadow-sm">
                          <span className="text-gray-900 font-medium">{winner.name}</span>
                          <span className="text-gray-500 text-sm">{winner.district}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                {language === 'en' 
                  ? 'No winners recorded yet' 
                  : 'ابھی تک کوئی فاتح درج نہیں ہوا'}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Award schemes with integrated award information
  const awardSchemes = [
    {
      id: 1,
      title: language === 'en' ? "Moulana Azad National Award" : "مولانا آزاد نیشنل ایوارڈ",
      description: language === 'en'
        ? "An annual award of Rs.2,50,000 presented on November 11th to distinguished individuals contributing to the development of Urdu language and literature. Includes a cash prize, memento, and certificate."
        : "اردو زبان و ادب کی ترقی میں نمایاں کردار ادا کرنے والے افراد کو 11 نومبر کو 2,50,000 روپے کا سالانہ ایوارڈ دیا جاتا ہے، جس میں نقد انعام، یادگار اور سرٹیفکیٹ شامل ہیں۔",
      awardName: "MAULANA AZAD NATIONAL AWARD"
    },
    {
      id: 2,
      title: language === 'en' ? "Dr. Abdul Haq Award" : "دکتر ابھر حاکم ایوارڈ",
      description: language === 'en'
        ? "An annual award of Rs.1,25,000 given to outstanding poets and writers of prose and poetry. Includes a cash prize, memento, and certificate."
        : "نثر اور شاعری کے نمایاں مصنفین اور شاعروں کو 1,25,000 روپے کا سالانہ ایوارڈ دیا جاتا ہے۔ اس میں نقد انعام، یادگار اور سرٹیفکیٹ شامل ہیں۔"
  ,
  awardName: "Dr. ABDUL HAQ AWARD"
    },
    {
      id: 3,
      title: language === 'en' ? "Life Time Achievement Award" : "لائف ٹائم اچیومنٹ ایوارڈ",
      description: language === 'en'
        ? "A Rs.25,000 award recognizing exceptional work in prose, poetry, history, fiction, research, and journalism. Includes a cash prize, memento, and certificate."
        : "نثر، شاعری، تاریخ، فکشن، تحقیق اور صحافت میں شاندار کام کے لیے 25,000 روپے کا ایوارڈ دیا جاتا ہے، جس میں نقد انعام، یادگار اور سرٹیفکیٹ شامل ہیں۔",
      awardName: "LIFETIME ACHIEVEMENT AWARD",
      subAwards: [
        {
          id: "dr-abdul-haq",
          name: "Dr. Abdul Haq Award",
          awardName: "Dr. ABDUL HAQ AWARD"
        },
        {
          id: "hazrath-shah-kamal",
          name: "Hazrath Shah Kamal Award",
          awardName: "Hazrath Shah Kamal Award (Poetry)"
        },
        {
          id: "allama-yaseer-kunooli",
          name: "Allama Yaseer Kunooli Award",
          awardName: "Allama Yaseer Kunooli Award (Poetry)"
        },
        {
          id: "sulaiman-ather-javeed",
          name: "Sulaiman Ather Javeed Award",
          awardName: "Sulaiman Ather Javeed Award (Research & Criticism)"
        },
        {
          id: "durvesh-khadar-zaki",
          name: "Durvesh Khadar Zaki Award",
          awardName: "Durvesh Khadar Zaki Award (Education & Teaching)"
        },
        {
          id: "hazrath-shah-filofer-quadri",
          name: "Hazrath Shah Filofer Quadri Award",
          awardName: "Hazrath Shah Filofer Quadri(RAH) Award (Prose)"
        },
        {
          id: "mirza-faiq-turkamani",
          name: "Mirza Faiq Turkamani Award",
          awardName: "Mirza Faiq Turkamani Award (Development of Urdu)"
        },
        {
          id: "yusuf-safi",
          name: "Yusuf Safi Award",
          awardName: "Yusuf Safi Award (Development of Urdu)"
        }
      ]
    },
    {
      id: 4,
      title: language === 'en' ? "Best Urdu Teacher Award" : "بہترین اردو ٹیچر ایوارڈ",
      description: language === 'en'
        ? "An annual award of Rs.10,000 given to outstanding Urdu teachers from primary to university levels. Presented on November 11th along with a shawl, memento, and certificate."
        : "پرائمری سے یونیورسٹی لیول تک کے بہترین اردو اساتذہ کو 11 نومبر کو 10,000 روپے کا سالانہ ایوارڈ دیا جاتا ہے، جس میں شال، یادگار اور سرٹیفکیٹ شامل ہیں۔",
      awardName: "Best Teacher Award"
    },
    {
      id: 5,
      title: language === 'en' ? "Best Urdu Student Award" : "بہترین اردو طالب علم ایوارڈ",
      description: language === 'en'
        ? "An annual award of Rs.5,000 given to SSC toppers. Includes a cash prize, shawl, memento, and certificate."
        : "ایس ایس سی ٹاپرز کو ہر سال 5,000 روپے کا ایوارڈ دیا جاتا ہے، جس میں نقد انعام، شال، یادگار اور سرٹیفکیٹ شامل ہیں۔",
      awardName: "Best Student Award"
    },
    {
      id: 8,
      title: language === 'en' ? "Grant-in-Aid to Urdu Organizations" : "اردو تنظیموں کے لیے گرانٹ ان ایڈ",
      description: language === 'en'
        ? "Grant-in-aid is provided to Urdu voluntary organizations for the preservation and promotion of Urdu language and literature."
        : "اردو زبان اور ادب کے تحفظ اور فروغ کے لیے اردو رضاکارانہ تنظیموں کو گرانٹ ان ایڈ فراہم کی جاتی ہے۔"
    },
  ];
  

  // Scheme cards for the schemes tab
  const schemeCards = [
    {
      id: 1,
      title: language === 'en' ? "Preserving / Promotion of Urdu Language" : "اردو زبان کا تحفظ / فروغ",
      description: language === 'en'
        ? "Initiatives and programs aimed at preserving and promoting the Urdu language through various educational and cultural activities."
        : "مختلف تعلیمی اور ثقافتی سرگرمیوں کے ذریعے اردو زبان کے تحفظ اور فروغ کے لیے اقدامات اور پروگرام۔"
    },
    {
      id: 2,
      title: language === 'en' ? "Vocational Training in Urdu Computer Education" : "اردو کمپیوٹر تعلیم میں پیشہ ورانہ تربیت",
      description: language === 'en'
        ? "Vocational training programs offered by the Urdu Academy to provide computer education in Urdu, enhancing digital literacy among Urdu speakers."
        : "اردو اکیڈمی کی جانب سے پیش کردہ پیشہ ورانہ تربیتی پروگرام جو اردو میں کمپیوٹر تعلیم فراہم کرتے ہیں، اردو بولنے والوں میں ڈیجیٹل خواندگی کو بڑھاتے ہیں۔"
    },
    {
      id: 3,
      title: language === 'en' ? "Generating Awareness About Plan Schemes" : "منصوبہ اسکیموں کے بارے میں آگاہی پیدا کرنا",
      description: language === 'en'
        ? "Initiatives to generate awareness about plan schemes of Minorities Welfare Department and other institutions among minorities."
        : "اقلیتی فلاحی محکمہ اور دیگر اداروں کی منصوبہ اسکیموں کے بارے میں اقلیتوں میں آگاہی پیدا کرنے کے اقدامات۔"
    },
    {
      id: 4,
      title: language === 'en' ? "Construction of Urdu Ghar-cum-Shadikhanas" : "اردو گھر کم شادی خانوں کی تعمیر",
      description: language === 'en'
        ? "Financial assistance is provided for the construction and renovation of Urdu Ghar-cum-Shadikhanas in Andhra Pradesh."
        : "آندھرا پردیش میں اردو گھر کم شادی خانوں کی تعمیر اور تزئین و آرائش کے لیے مالی امداد فراہم کی جاتی ہے۔"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="relative.">
        <div className="h-[300px] relative">
          <img 
            src="https://th-i.thgim.com/public/migration_catalog/article10456065.ece/alternates/FREE_1200/amaravathi"
            alt="Academy Schemes Header" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-blue-900/40">
            <div className="container mx-auto px-8 h-full flex items-center">
              <div className={`max-w-2xl text-white ${language === 'ur' ? 'text-right' : 'text-left'}`}>
                <h1 className="text-3xl md:text-5xl font-bold mb-4">
                  {language === 'en' ? "Academy Awards & Schemes" : "اکیڈمی ایوارڈز اور اسکیمز"}
                </h1>
                <p className="text-lg md:text-xl">
                  {language === 'en'
                    ? "Recognizing excellence in Urdu language and literature"
                    : "اردو زبان و ادب میں امتیاز کی شناخت"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <div className="container mx-auto px-8 py-6 md:py-12">
        <div className="flex flex-wrap gap-2 sm:gap-4 justify-center mb-6">
          {['awards', 'schemes'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 sm:px-6 lg:px-8 py-2 rounded-lg ${
                activeTab === tab ? 'bg-yellow-300 text-black' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {language === 'en'
                ? tab.charAt(0).toUpperCase() + tab.slice(1)
                : tab === 'awards' ? 'ایوارڈز' : 'اسکیمز'}
            </button>
          ))}
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {activeTab === 'awards' && (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {awardSchemes.map((scheme) => {
            // For Lifetime Achievement Award, check if any sub-awards have winners
            let hasWinners = false;
            if (scheme.awardName === "LIFETIME ACHIEVEMENT AWARD" && scheme.subAwards) {
              hasWinners = scheme.subAwards.some(subAward => 
                Object.keys(getWinnersForAward(subAward.awardName)).length > 0
              );
            } else {
              // For regular awards
              hasWinners = scheme.awardName && Object.keys(getWinnersForAward(scheme.awardName)).length > 0;
            }
            return (
              <div
                key={scheme.id}
                className="bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-300 hover:shadow-lg hover:-translate-y-1"
              >
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{scheme.title}</h3>
                  <p className="text-gray-600 mb-4">{scheme.description}</p>
                  {hasWinners && (
                    <button
                      onClick={() => {
                        setSelectedScheme(scheme);
                        setIsModalOpen(true);
                      }}
                      className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      {language === 'en' ? 'View Awardees' : 'فاتحین دیکھیں'}
                      <svg className="ml-2 -mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          </div>
        )}

        {activeTab === 'schemes' && (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {schemeCards.map((scheme) => (
              <div
                key={scheme.id}
                className="bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-300 hover:shadow-lg hover:-translate-y-1"
              >
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{scheme.title}</h3>
                  <p className="text-gray-600 mb-4">{scheme.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {isModalOpen && <WinnersModal scheme={selectedScheme} onClose={() => {
        setIsModalOpen(false);
        setSelectedScheme(null);
      }} />}
    </div>
  );
};

export default Schemes;